import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Save, User, ArrowRight, Search } from "lucide-react";
import { toast } from "sonner";
import PatientSearchHeader from "@/components/PatientSearchHeader";

const emptyRefraction = { sph: "", cyl: "", axis: "", add: "", va: "" };

function RefractionBlock({ title, val, onChange }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-brand">{title}</div>
      <div className="grid grid-cols-5 gap-2">
        {["sph","cyl","axis","add","va"].map(k => (
          <div key={k}>
            <Label className="text-[10px] uppercase text-ink-subtle">{k}</Label>
            <Input value={val[k] || ""} onChange={(e) => onChange({ ...val, [k]: e.target.value })} className="h-8" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Optometry() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const visitId = params.get("visit") || "";
  const [form, setForm] = useState({
    visit_id: visitId,
    auto_refraction_od: { ...emptyRefraction }, auto_refraction_os: { ...emptyRefraction },
    subjective_od: { ...emptyRefraction }, subjective_os: { ...emptyRefraction },
    distant_vision_od: "", distant_vision_os: "",
    near_vision_od: "", near_vision_os: "",
    pd: "", keratometry_od: "", keratometry_os: "", lensometry: "", color_vision: "",
    glass_rx: "", contact_lens_rx: "", remarks: "",
  });
  const [saving, setSaving] = useState(false);
  const [patient, setPatient] = useState(null);
  // Patient picker state (when there's no visit selected)
  const [queue, setQueue] = useState([]);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(false);

  useEffect(() => {
    if (!visitId) {
      // Load today's OPD queue so the optometrist can pick the next patient
      setLoadingQueue(true);
      api.get("/patients/visits/queue/today").then(({ data }) => setQueue(data.items)).finally(() => setLoadingQueue(false));
      return;
    }
    (async () => {
      try {
        const { data: ex } = await api.get(`/opd/optometry/by-visit/${visitId}`);
        if (ex && ex.id) setForm(s => ({ ...s, ...ex }));
        const queue = await api.get("/patients/visits/queue/today");
        const v = queue.data.items.find(x => x.id === visitId);
        if (v) {
          const pat = await api.get(`/patients/${v.patient_id}`);
          setPatient(pat.data.patient);
        }
      } catch { /* ignore */ }
    })();
  }, [visitId]);

  const doSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) { setSearchResults([]); return; }
    const { data } = await api.get(`/patients?q=${encodeURIComponent(q)}&limit=10`);
    setSearchResults(data.items || []);
  };

  const pickVisit = (visitId) => setParams({ visit: visitId });

  const startStandaloneAssessment = async (patientObj) => {
    // Create an "optometry-only" visit so the assessment can be saved and linked when a doctor sees the patient.
    try {
      const rv = await api.post(`/patients/${patientObj.id}/visits`, { patient_id: patientObj.id, chief_complaint: "Optometry-only assessment" });
      setParams({ visit: rv.data.id });
      toast.success(`Visit #${rv.data.token_no} opened for ${patientObj.name}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const save = async () => {
    if (!visitId) { toast.error("Open optometry from an OPD visit (?visit=...)"); return; }
    setSaving(true);
    try {
      await api.post("/opd/optometry", { ...form, visit_id: visitId });
      toast.success("Optometry assessment saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!visitId) {
    // Visit-status → card background colour mapping
    const statusBg = (s) => {
      const k = (s || "").toLowerCase();
      if (k === "completed") return "bg-emerald-100 border-emerald-300 hover:border-emerald-500";
      if (k === "in_consultation") return "bg-amber-50 border-amber-300 hover:border-amber-500";
      if (k === "called") return "bg-violet-50 border-violet-300 hover:border-violet-500";
      // waiting / default
      return "bg-white border-border hover:border-brand";
    };
    return (
      <div className="space-y-6 animate-fade-in" data-testid="optometry-picker">
        <div>
          <div className="section-label">Optometry · Patient selection</div>
          <h1 className="font-heading text-3xl font-semibold">Choose a patient for refraction</h1>
          <p className="mt-1 text-sm text-ink-muted">Pick from today's OPD queue or search any patient. Your findings auto-link to the doctor's consultation.</p>
        </div>

        {/* Top header — search any patient (cross-branch lookup) */}
        <PatientSearchHeader
          label="OR SEARCH ANY PATIENT (across all branches)"
          placeholder="Type name, UHID or mobile (min 2 chars)…"
          crossBranch
          testIdRoot="optometry-header-search"
          onPick={(p) => startStandaloneAssessment(p)}
        />

        <Card className="p-4" data-testid="optometry-queue-picker">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-brand">Today's OPD queue</div>
            <div className="flex items-center gap-3 text-[10px] text-ink-muted">
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-white ring-1 ring-border" /> Waiting</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-violet-200" /> Called</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-200" /> In consultation</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-200" /> Completed</span>
            </div>
          </div>
          {loadingQueue && <div className="py-4 text-center text-xs text-ink-muted">Loading…</div>}
          {!loadingQueue && queue.length === 0 && <div className="py-4 text-center text-xs text-ink-muted">No visits today. Use search above to start an optometry-only assessment.</div>}
          {queue.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {queue.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => pickVisit(v.id)}
                  className={`flex items-center justify-between rounded-md border-2 p-3 text-left transition ${statusBg(v.status)}`}
                  data-testid={`optometry-pick-${v.token_no}`}
                >
                  <div>
                    <div className="font-heading text-lg text-brand">#{v.token_no}</div>
                    <div className="text-sm font-medium">{v.patient_name}</div>
                    <div className="text-[10px] text-ink-muted">UHID {v.patient_uhid}</div>
                    {v.doctor_name && <div className="text-[10px] text-ink-muted">→ Dr. {v.doctor_name}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant="outline" className="bg-white/70 text-[10px] capitalize">{(v.status || "").replace("_", " ")}</Badge>
                    <ArrowRight size={14} className="text-brand" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700">
          <b>Tip:</b> Once you save the refraction, it will be auto-loaded when the doctor opens this patient's consultation.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in pb-24">
      <div>
        <div className="section-label">Optometry assessment</div>
        <h1 className="font-heading text-3xl font-semibold">Optometry / Refraction</h1>
        {patient && <div className="mt-1 text-sm text-ink-muted">{patient.name} · <span className="font-mono text-xs">{patient.uhid}</span></div>}
        <button onClick={() => navigate("/app/optometry")} className="mt-1 text-[11px] text-brand hover:underline" data-testid="optometry-change-patient">← change patient</button>
      </div>

      <Card className="p-5">
        <div className="section-label mb-3">Auto refraction</div>
        <div className="grid gap-3 md:grid-cols-2">
          <RefractionBlock title="OD" val={form.auto_refraction_od} onChange={v => set("auto_refraction_od", v)} />
          <RefractionBlock title="OS" val={form.auto_refraction_os} onChange={v => set("auto_refraction_os", v)} />
        </div>
      </Card>

      <Card className="p-5">
        <div className="section-label mb-3">Subjective refraction</div>
        <div className="grid gap-3 md:grid-cols-2">
          <RefractionBlock title="OD" val={form.subjective_od} onChange={v => set("subjective_od", v)} />
          <RefractionBlock title="OS" val={form.subjective_os} onChange={v => set("subjective_os", v)} />
        </div>
      </Card>

      <Card className="p-5">
        <div className="section-label mb-3">Vision & measurements</div>
        <div className="form-grid">
          <div><Label>Distant vision OD</Label><Input value={form.distant_vision_od} onChange={e => set("distant_vision_od", e.target.value)} /></div>
          <div><Label>Distant vision OS</Label><Input value={form.distant_vision_os} onChange={e => set("distant_vision_os", e.target.value)} /></div>
          <div><Label>Near vision OD</Label><Input value={form.near_vision_od} onChange={e => set("near_vision_od", e.target.value)} /></div>
          <div><Label>Near vision OS</Label><Input value={form.near_vision_os} onChange={e => set("near_vision_os", e.target.value)} /></div>
          <div><Label>Pupillary distance (mm)</Label><Input value={form.pd} onChange={e => set("pd", e.target.value)} /></div>
          <div><Label>Keratometry OD</Label><Input value={form.keratometry_od} onChange={e => set("keratometry_od", e.target.value)} /></div>
          <div><Label>Keratometry OS</Label><Input value={form.keratometry_os} onChange={e => set("keratometry_os", e.target.value)} /></div>
          <div><Label>Lensometry</Label><Input value={form.lensometry} onChange={e => set("lensometry", e.target.value)} /></div>
          <div><Label>Color vision</Label><Input value={form.color_vision} onChange={e => set("color_vision", e.target.value)} /></div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="section-label mb-3">Prescription</div>
        <div className="form-grid">
          <div className="md:col-span-2"><Label>Glass Rx</Label><Textarea rows={2} value={form.glass_rx} onChange={e => set("glass_rx", e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Contact lens Rx</Label><Textarea rows={2} value={form.contact_lens_rx} onChange={e => set("contact_lens_rx", e.target.value)} /></div>
          <div className="md:col-span-3"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={e => set("remarks", e.target.value)} /></div>
        </div>
      </Card>

      <div className="sticky bottom-0 -mx-4 flex items-center justify-between border-t border-border bg-white px-4 py-3 shadow-[0_-2px_8px_-2px_rgba(0,0,0,0.05)] md:-mx-6 lg:-mx-8">
        <div className="text-xs text-ink-muted">Entries time-stamped & audit-logged (NABH).</div>
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-700" data-testid="optometry-save-btn"><Save size={14} className="mr-2" />{saving ? "Saving…" : "Save assessment"}</Button>
      </div>
    </div>
  );
}
